import { Box, Button, Checkbox, Flex, Heading, Text } from '@chakra-ui/react';
import PrivateAccountForm from '../PrivateAccountForm/PrivateAccountForm';
import CompanyDetailsForm from '../CompanyDetailsForm/CompanyDetailsForm';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../../../shared/providers/AuthProvider/AuthProvider";
import { useEffect, useState, useCallback, useRef } from 'react';
import { useAppToast } from "../../../../hooks/useAppToast";

const AccountSetupScreen = () => {
    const { user, getSetup } = useAuth();
    const navigate = useNavigate();
    const [config, setConfig] = useState({});
    const [accountType, setAccountType] = useState('private');
    const toast = useAppToast();
    const isInitialized = useRef(false);
    const hasUserChangedType = useRef(false);
    const { t } = useTranslation('default');

    const fetchSetup = useCallback(async () => {
        if (user) {
            try {
                const res = await getSetup();
                if (res && res.data) {
                    setConfig(res.data);
                    
                    // Tylko przy pierwszym załadowaniu weźmiemy typ konta z API,
                    // pod warunkiem że w trakcie ładowania (po wejściu na stronę) użytkownik nie zdążył pobawić się tabami.
                    if (!isInitialized.current) {
                        if (!hasUserChangedType.current) {
                            setAccountType(res.data.accountType ?? 'private');
                        }
                        isInitialized.current = true;
                    }
                }
            } catch (e) {
                toast({
                    title: 'Błąd podczas pobierania danych',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, getSetup]);

    useEffect(() => {
        fetchSetup();
    }, [fetchSetup]);

    const handleAccountConfigured = async () => {
        toast({
            title: t('form.success'),
            status: 'success',
            duration: 3000,
            isClosable: true
        });
        await fetchSetup();
        navigate('/');
    };

    return (
        <Box display={"flex"} flexDirection={"column"} gap={'32px'} className="setup">
            <Flex flexDirection={"column"} className="title" gap={'16px'}>
                <Text className="title">
                    {t('settings.title')}
                </Text>
                <Box className="line"></Box>
            </Flex>

            <Flex flexDirection={"column"} pb="32px" pt={'24px'} px={'24px'} bgColor={"white"} borderRadius={'4px'}>
                <Flex flexDirection={"column"} className="header">
                    <Text className="title">
                        {t('setup.form.accountType.title')}
                    </Text>
                    <Text className="description">
                        {t('setup.form.accountType.description')}
                    </Text>
                </Flex>
                <Flex>
                    <Box
                        px={4}
                        py={'16px'}
                        flex="1"
                        textAlign={"center"}
                        borderBottom={accountType === 'private' ? '3px solid #089A7A' : '3px solid #EEEEEE'}
                        cursor="pointer"
                        onClick={() => {
                            hasUserChangedType.current = true;
                            setAccountType('private');
                        }}
                    >
                        <Text fontWeight="medium">
                            <Checkbox
                                isChecked={accountType === 'private'}
                                pointerEvents="none"
                                mr={'10px'}
                                mt={'5px'}
                            />
                            {t('setup.form.accountType.private')}
                        </Text>
                    </Box>
                    <Box
                        px={4}
                        py={'16px'}
                        flex="1"
                        textAlign={"center"}
                        borderBottom={accountType === 'business' ? '3px solid #089A7A' : '3px solid #EEEEEE'}
                        cursor="pointer"
                        onClick={() => {
                            hasUserChangedType.current = true;
                            setAccountType('business');
                        }}
                    >
                        <Text fontWeight="medium">
                            <Checkbox
                                isChecked={accountType === 'business'}
                                pointerEvents="none"
                                mr={'10px'}
                                mt={'5px'}
                            />
                            {t('setup.form.accountType.business')}
                        </Text>
                    </Box>
                </Flex>
            </Flex>

            {accountType === 'private' && <PrivateAccountForm onSuccess={handleAccountConfigured} config={config} />}
            {accountType === 'business' && <CompanyDetailsForm onSuccess={handleAccountConfigured} config={config} user={user} />}
        </Box>
    );
};

export default AccountSetupScreen;
